import { useEffect, useRef, useState } from 'react';
import TrackMap from '../TrackMap/TrackMap';
import styles from './PublicTrack.module.css';

/**
 * The satellite track map on a public /tracks/<slug> page.
 *
 * Two things make this different from every other TrackMap mount:
 *
 * **It replaces prerendered content rather than filling an empty box.** The
 * static HTML already contains an inline SVG of the layout — that is what search
 * engines index and what a visitor with JavaScript off sees. This upgrades it to
 * the live satellite view, and only once the section is actually scrolled into
 * view. Google Maps is billed per load and the API key ships in the bundle on a
 * page we are inviting the whole internet to; a crawler or a bounced visitor
 * should never cost a map load.
 *
 * **Turns are off.** `corners={[]}` with `showTurnMarkers={false}`, because the
 * stored corner definitions are detection-derived and disagree with several of
 * these circuits' real turn numbering. The speed coloring carries the analytic
 * weight instead, and needs no turn numbers to be true.
 */
export default function TrackMapIsland({ trace, staticMap }) {
  const hostRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = hostRef.current;
    if (!el) return undefined;
    if (typeof IntersectionObserver === 'undefined') {
      // No observer: show it rather than withholding the feature. This path is
      // old browsers, not bots — bots don't run this file at all.
      setVisible(true);
      return undefined;
    }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        setVisible(true);
        observer.disconnect();
      }
    }, { rootMargin: '200px' });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const ready = visible && (trace?.points?.length || 0) > 3;

  return (
    <div ref={hostRef}>
      {ready ? (
        <div className={styles.mapBox}>
          <TrackMap
            lapTraces={{ 0: trace }}
            selectedLaps={[0]}
            corners={[]}
            canEditCorners={false}
            showTurnMarkers={false}
            initialColorMode="speed"
          />
        </div>
      ) : (
        // The prerendered block, handed back verbatim so the swap has nothing to
        // flash through. It carries its own speed legend, which the live map
        // draws for itself — hence replacing the whole block, not just the SVG.
        <div
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: staticMap || '' }}
        />
      )}
    </div>
  );
}
